import React, { useState } from "react";
import { FiEdit3, FiEye, FiEyeOff, FiX, FiClock } from "react-icons/fi";
import { userApiService } from "../../../service/admin/userApiService";
import { useMessage, ToastTypes } from "../../../base/context/MessageProvider";
import { useLoader } from "../../../base/context/LoaderProvider";
import { useNavigate } from "react-router-dom";

const WalletEditForUser = ({ user, onUpdated }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [walletAmount, setWalletAmount] = useState("");
  const [walletPassword, setWalletPassword] = useState("");
  const [walletNote, setWalletNote] = useState("");
  const [operationType, setOperationType] = useState("add"); // 'add' or 'subtract'
  const [showWalletPassword, setShowWalletPassword] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [editHistory, setEditHistory] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(false);

  const message = useMessage();
  const loader = useLoader();
  const navigate = useNavigate();

  const fetchEditHistory = async () => {
    if (!user?._id) return;
    try {
      setLoadingHistory(true);
      const res = await userApiService.getWalletEditHistory(user._id);
      if (res.status.code === 200 && res.data) {
        setEditHistory(res.data || []);
      }
    } catch (err) {
      console.error("Error fetching edit history:", err);
    } finally {
      setLoadingHistory(false);
    }
  };

  const openModal = () => {
    setWalletAmount("");
    setWalletPassword("");
    setWalletNote("");
    setOperationType("add");
    setShowWalletPassword(false);
    setIsOpen(true);
    fetchEditHistory();
  };

  const closeModal = () => {
    setIsOpen(false);
    setIsSubmitting(false);
  };

  const handleViewHistory = () => {
    if (!user?.email) {
      message.showToast("User email not found for history", ToastTypes.ERROR);
      return;
    }
    // go to transactions page with email filter
    navigate("/admin/transactions", {
      state: { email: user.email },
    });
  };

  // create wallet for user if not exists
  const handleCreateWallet = async () => {
    try {
      loader.showLoading(true);
      const res = await userApiService.createWalletByUserId(user._id);
      loader.showLoading(false);

      if (res.status.code === 200) {
        message.showToast("Wallet created successfully", ToastTypes.SUCCESS);
        onUpdated && onUpdated();
        openModal();
      } else {
        message.showToast(
          res.error?.message || "Failed to create wallet",
          ToastTypes.ERROR
        );
      }
    } catch (err) {
      loader.showLoading(false);
      message.showToast(
        "Server error while creating wallet",
        ToastTypes.ERROR
      );
    }
  };

  // amount add/subtract by admin
  const handleAddAmount = async () => {
    if (isSubmitting) return;
    const amt = Number(walletAmount);
    if (!amt || amt <= 0) {
      message.showToast("Please enter valid amount", ToastTypes.ERROR);
      return;
    }
    if (!walletPassword.trim()) {
      message.showToast("Password is required", ToastTypes.ERROR);
      return;
    }

    try {
      setIsSubmitting(true); 
      loader.showLoading(true);
      const res = await userApiService.addWalletAmountByAdmin(
        user._id,
        amt,
        walletPassword,
        walletNote,
        operationType
      );
      loader.showLoading(false);

      if (res.status.code === 200) {
        message.showToast(
          `Wallet ${operationType === 'add' ? 'amount added' : 'amount deducted'} successfully`,
          ToastTypes.SUCCESS
        );
        closeModal();
        onUpdated && onUpdated();
      } else {
        message.showToast(
          res?.status?.message || res?.error?.message || "Failed to update wallet",
          ToastTypes.ERROR
        );
      }
    } catch (err) {
      loader.showLoading(false);
      setIsSubmitting(false);
      const apiMsg = err?.message;

      message.showToast(
        apiMsg ||
        "Server error while updating wallet",
        ToastTypes.ERROR
      );
    } finally {
      loader.showLoading(false);
      setIsSubmitting(false);  
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    return date.toLocaleString("en-IN", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  return (
    <>
      {user.hasWallet ? (
        <button
          type="button"
          className="inline-flex items-center gap-1 text-xs sm:text-sm text-blue-600 hover:text-blue-800"
          onClick={openModal}
        >
          <FiEdit3 className="inline-block" />
        </button>
      ) : (
         <button
          type="button"
          className="px-2 py-0.5 bg-green-500 text-white rounded-md hover:bg-green-600 text-xs"
          onClick={handleCreateWallet}
        >
          Create Wallet
        </button>
      )}

      {/* Modal */}
      {isOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4" onClick={closeModal}>
          <div className="w-full max-w-5xl rounded-2xl bg-white shadow-xl flex flex-col max-h-[90vh]" onClick={(e) => e.stopPropagation()}>
            {/* Header */}
            <div className="flex items-center justify-between border-b px-4 py-3 sm:px-6 flex-shrink-0">
              <div>
                <h2 className="text-base sm:text-lg font-semibold text-slate-900">
                  Edit Wallet Balance
                </h2>
                <p className="text-xs sm:text-sm text-slate-500">
                  {user.firstName} {user.lastName} ({user.email})
                </p>
              </div>
              <button
                type="button"
                className="p-1 rounded-full hover:bg-slate-100"
                onClick={closeModal}
              >
                <FiX />
              </button>
            </div>

            {/* Body - Two Column Layout */}
            <div className="flex flex-1 overflow-hidden">
              {/* Left Side - Previous Edit History */}
              <div className="w-1/2 border-r border-slate-200 flex flex-col">
                <div className="px-4 py-3 border-b border-slate-200 flex-shrink-0">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <FiClock className="text-slate-500" size={16} />
                      <h3 className="text-sm font-semibold text-slate-700">
                        Previous Edit History
                      </h3>
                    </div>
                    <button
                      type="button"
                      className="text-xs text-blue-600 hover:underline"
                      onClick={handleViewHistory}
                    >
                      View full Transaction history
                    </button>
                  </div>
                </div>
                <div className="flex-1 overflow-y-auto px-4 py-3 wallet-history-scroll">
                  {loadingHistory ? (
                    <div className="flex items-center justify-center h-full text-slate-500 text-sm">
                      Loading history...
                    </div>
                  ) : editHistory.length > 0 ? (
                    <div className="space-y-3">
                      {editHistory.map((history, idx) => (
                        <div
                          key={idx}
                          className="bg-slate-50 rounded-lg p-3 text-xs border border-slate-200"
                        >
                          <div className="flex justify-between items-start mb-2">
                            <div className="flex-1">
                              <div className="font-medium text-slate-700 mb-1">
                                {history.adminEmail || "Admin"}
                              </div>
                              {history.adminNote && (
                                <div className="text-slate-600 mt-1 text-xs">
                                  {history.adminNote}
                                </div>
                              )}
                            </div>
                            <div className="text-right ml-2">
                              <div
                                className={`font-semibold text-sm ${
                                  history.amount?.startsWith("+")
                                    ? "text-green-600"
                                    : "text-red-600"
                                }`}
                              >
                                {history.amount?.startsWith("+") ? "+" : ""}₹
                                {Math.abs(parseFloat(history.amount || 0)).toLocaleString(
                                  "en-IN"
                                )}
                              </div>
                              <div className="text-slate-500 text-xs mt-1">
                                {formatDate(history.created_at)}
                              </div>
                            </div>
                          </div>
                          <div className="text-slate-500 text-xs mt-1 pt-1 border-t border-slate-200">
                            Balance after: ₹
                            {Number(history.balance || 0).toLocaleString("en-IN")}
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="flex items-center justify-center h-full text-slate-400 text-sm">
                      No edit history found
                    </div>
                  )}
                </div>
              </div>

              {/* Right Side - Add/Subtract Form */}
              <div className="w-1/2 flex flex-col">
                <div className="px-4 py-4 sm:px-6 sm:py-5 space-y-4 flex-1 overflow-y-auto">
                  {/* Current Balance */}
                  <div className="flex items-center justify-between text-sm pb-3 border-b border-slate-200">
                    <span className="text-slate-500">Current Balance</span>
                    <span className="font-semibold text-slate-900 text-lg">
                      ₹{Number(user.walletBalance ?? 0).toLocaleString("en-IN")}
                    </span>
                  </div>

                  {/* Operation Type Toggle */}
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">
                      Operation Type
                    </label>
                    <div className="flex gap-2">
                      <button
                        type="button"
                        onClick={() => setOperationType("add")}
                        className={`flex-1 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                          operationType === "add"
                            ? "bg-green-600 text-white"
                            : "bg-slate-100 text-slate-700 hover:bg-slate-200"
                        }`}
                      >
                        Add Amount
                      </button>
                      <button
                        type="button"
                        onClick={() => setOperationType("subtract")}
                        className={`flex-1 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                          operationType === "subtract"
                            ? "bg-red-600 text-white"
                            : "bg-slate-100 text-slate-700 hover:bg-slate-200"
                        }`}
                      >
                        Subtract Amount
                      </button>
                    </div>
                  </div>

                  {/* Amount */}
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">
                      Amount ({operationType === "add" ? "to Add" : "to Subtract"}) (₹)
                    </label>
                    <input
                      type="number"
                      min="1"
                      className="block w-full rounded-lg border border-slate-300 px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      value={walletAmount}
                      onChange={(e) => setWalletAmount(e.target.value)}
                      placeholder="Enter amount"
                    />
                  </div>

                  {/* Note Field */}
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">
                      Note / Message
                    </label>
                    <textarea
                      rows="3"
                      className="block w-full rounded-lg border border-slate-300 px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-none"
                      value={walletNote}
                      onChange={(e) => setWalletNote(e.target.value)}
                      placeholder="Enter note or message for this transaction"
                    />
                  </div>

                  {/* Password */}
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">
                      Security PIN
                    </label>
                    <div className="relative">
                      <input
                        type={showWalletPassword ? "text" : "password"}
                        className="block w-full rounded-lg border border-slate-300 px-3 py-2 pr-10 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        value={walletPassword}
                        onChange={(e) => setWalletPassword(e.target.value)}
                        placeholder="Enter Security PIN"
                        name="adminWalletPin"        
                        autoComplete="new-password" 
                        inputMode="text"
                      />
                      <button
                        type="button"
                        className="absolute inset-y-0 right-0 flex items-center pr-3 text-slate-500 hover:text-slate-700"
                        onClick={() => setShowWalletPassword((p) => !p)}
                      >
                        {showWalletPassword ? (
                          <FiEyeOff size={16} />
                        ) : (
                          <FiEye size={16} />
                        )}
                      </button>
                    </div>
                  </div>

                  <p className="text-xs text-slate-400">
                    {operationType === "add"
                      ? "Wallet amount will be added as a manual deposit for this user."
                      : "Amount will be deducted from user's wallet balance."}
                  </p>
                </div>

                {/* Footer */}
                <div className="flex flex-col-reverse gap-2 border-t px-4 py-3 sm:flex-row sm:justify-end sm:px-6 flex-shrink-0">
                  <button
                    type="button"
                    className="w-full sm:w-auto rounded-lg border border-slate-300 px-4 py-2 text-sm font-medium text-slate-700 bg-white hover:bg-slate-100"
                    onClick={closeModal}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    disabled={isSubmitting}
                    className="w-full sm:w-auto rounded-lg bg-blue-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-blue-700 disabled:opacity-60 disabled:cursor-not-allowed"
                    onClick={handleAddAmount}
                  >
                    {isSubmitting
                      ? "Processing..."
                      : operationType === "add"
                      ? "Add Amount"
                      : "Subtract Amount"}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default WalletEditForUser;
